import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Link,
  Tabs,
  Tab,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../hooks/useAuth';
import { studentGuideContent } from '../../content/student-guide';
import { instructorGuideContent } from '../../content/instructor-guide';
import { adminGuideContent } from '../../content/admin-guide';

const adminTabs = [
  { label: 'Admin & Architecture', content: adminGuideContent, filename: 'praxis-admin-guide.md' },
  { label: 'Instructor Guide', content: instructorGuideContent, filename: 'praxis-instructor-guide.md' },
  { label: 'Student Guide', content: studentGuideContent, filename: 'praxis-student-guide.md' },
];

const instructorTabs = [
  { label: 'Instructor Guide', content: instructorGuideContent, filename: 'praxis-instructor-guide.md' },
  { label: 'Student Guide', content: studentGuideContent, filename: 'praxis-student-guide.md' },
];

const markdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="h4" sx={{ mt: 4, mb: 2, fontWeight: 700 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 600 }}>
      {children}
    </Typography>
  ),
  h4: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
      {children}
    </Typography>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <Link href={href} color="primary" underline="hover">
      {children}
    </Link>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small">{children}</Table>
    </TableContainer>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }: { children?: React.ReactNode }) => <TableBody>{children}</TableBody>,
  tr: ({ children }: { children?: React.ReactNode }) => <TableRow>{children}</TableRow>,
  th: ({ children }: { children?: React.ReactNode }) => (
    <TableCell sx={{ fontWeight: 600, bgcolor: 'action.hover' }}>
      {children}
    </TableCell>
  ),
  td: ({ children }: { children?: React.ReactNode }) => <TableCell>{children}</TableCell>,
  code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
    const isBlock = className?.startsWith('language-');
    if (isBlock) {
      return (
        <Box
          component="code"
          sx={{
            display: 'block',
            bgcolor: '#1e1e2e',
            color: '#cdd6f4',
            p: 2,
            borderRadius: 1,
            overflowX: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            lineHeight: 1.6,
          }}
        >
          {children}
        </Box>
      );
    }
    return (
      <Box
        component="code"
        sx={{
          bgcolor: 'grey.100',
          px: 0.75,
          py: 0.25,
          borderRadius: 0.5,
          fontFamily: 'monospace',
          fontSize: '0.875em',
        }}
      >
        {children}
      </Box>
    );
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    <Box sx={{ mb: 2 }}>{children}</Box>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <Box
      sx={{
        borderLeft: 4,
        borderColor: 'primary.main',
        bgcolor: 'action.hover',
        pl: 2,
        py: 1,
        my: 2,
        borderRadius: '0 4px 4px 0',
      }}
    >
      {children}
    </Box>
  ),
  hr: () => <Divider sx={{ my: 3 }} />,
  li: ({ children }: { children?: React.ReactNode }) => (
    <li>
      <Typography variant="body1" component="span">
        {children}
      </Typography>
    </li>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      {children}
    </Box>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <Box component="ol" sx={{ pl: 3, mb: 2 }}>
      {children}
    </Box>
  ),
};

const DocumentationPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);

  const isAdmin = user?.role === 'admin';
  const isStudent = user?.role === 'student';
  const isInstructor = user?.role === 'instructor';

  const content = isAdmin
    ? adminTabs[activeTab].content
    : isInstructor
      ? instructorTabs[activeTab].content
      : studentGuideContent;

  const filename = isAdmin
    ? adminTabs[activeTab].filename
    : isInstructor
      ? instructorTabs[activeTab].filename
      : 'praxis-student-guide.md';

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {isAdmin ? (
        <>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
            Documentation
          </Typography>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 1 }}>
            <Tab label="Admin & Architecture" />
            <Tab label="Instructor Guide" />
            <Tab label="Student Guide" />
          </Tabs>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
              Download Guide
            </Button>
          </Box>
        </>
      ) : isInstructor ? (
        <>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
            Documentation
          </Typography>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 1 }}>
            <Tab label="Instructor Guide" />
            <Tab label="Student Guide" />
          </Tabs>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
              Download Guide
            </Button>
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Documentation
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download Guide
          </Button>
        </Box>
      )}

      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {content}
        </Markdown>
      </Box>
    </Box>
  );
};

export default DocumentationPage;
